import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  MenuItem,
  Button,
  CircularProgress,
  IconButton,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  AttachMoney as MoneyIcon,
  Receipt as ReceiptIcon,
  AccountBalance as VATIcon,
  ArrowBack as BackIcon,
  ShoppingCart as SalesIcon,
} from '@mui/icons-material';
import ReportDataGrid from '../components/ReportDataGrid';
import reportService from '../services/reportService';
import customerService from '../services/customerService';
import { useNavigate } from 'react-router-dom';
import riyalIcon from '../assets/riyal.png';

const VATSalesReport = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [totals, setTotals] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [filters, setFilters] = useState({
    groupBy: 'day',
    startDate: new Date(),
    endDate: new Date(),
    customerCode: 'all',
  });

  // Calculate date range based on groupBy selection
  const getDateRangeForGroupBy = (groupBy) => {
    const today = new Date();
    let startDate, endDate;

    switch (groupBy) {
      case 'day':
        startDate = new Date(today);
        endDate = new Date(today);
        break;
      case 'week':
        startDate = new Date(today);
        startDate.setDate(today.getDate() - today.getDay());
        endDate = new Date(today);
        break;
      case 'month':
        startDate = new Date(today.getFullYear(), today.getMonth(), 1);
        endDate = new Date(today);
        break;
      case 'year':
        startDate = new Date(today.getFullYear(), 0, 1);
        endDate = new Date(today);
        break;
      default:
        startDate = new Date(today);
        endDate = new Date(today);
    }
    return { startDate, endDate };
  };

  const fetchCustomers = async () => {
    try {
      const response = await customerService.getCustomers(1, 0);
      if (response && response.customers) {
        setCustomers(response.customers);
      } else if (response.success && response.data) {
        setCustomers(response.data);
      }
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  // Fetch report data
  const fetchReport = async () => {
    try {
      setLoading(true);
      const formatDate = (date) => {
        if (!date) return undefined;
        const d = new Date(date);
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };

      const params = {
        startDate: formatDate(filters.startDate),
        endDate: formatDate(filters.endDate),
      };

      if (filters.customerCode && filters.customerCode !== 'all') {
        params.customerCode = filters.customerCode;
      }

      const response = await reportService.getVATSales(params);

      if (response.success) {
        // Flatten the data to show individual invoices
        const flattenedData = [];
        if (response.data) {
          response.data.forEach((periodGroup) => {
            if (periodGroup.invoices && periodGroup.invoices.length > 0) {
              periodGroup.invoices.forEach((invoice) => {
                flattenedData.push({
                  ...invoice,
                  period: periodGroup.period,
                });
              });
            }
          });
        }
        setReportData(flattenedData);
        setTotals(response.totals || null);
      }
    } catch (error) {
      console.error('Error fetching VAT sales report:', error);
      setReportData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomers();
    fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFilterChange = (field, value) => {
    if (field === 'groupBy') {
      const { startDate, endDate } = getDateRangeForGroupBy(value);
      setFilters(prev => ({
        ...prev,
        [field]: value,
        startDate,
        endDate
      }));
    } else {
      setFilters(prev => ({ ...prev, [field]: value }));
    }
  };

  const handleApplyFilters = () => {
    fetchReport();
  };

  const columns = [
    {
      field: 'invoiceDate',
      headerName: 'Date',
      flex: 0.8,
      minWidth: 110,
      sortable: true,
      valueFormatter: ({ value }) => value ? new Date(value).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '-',
    },
    {
      field: 'invoiceNumber',
      headerName: 'Invoice No',
      flex: 0.8,
      minWidth: 120,
      sortable: true,
    },
    {
      field: 'customerName',
      headerName: 'Customer',
      flex: 1.2,
      minWidth: 180,
      sortable: true,
      renderCell: (params) => params.row.customerCode === null || params.row.customerCode === undefined ? 'Walk-in Customer' : (params.value || '-'),
    },
    {
      field: 'subTotal',
      headerName: 'Amount',
      flex: 0.9,
      minWidth: 120,
      type: 'number',
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5, width: '100%' }}>
          <img src={riyalIcon} alt="currency" style={{ width: '15px', height: '15px' }} />
          <span>{params.value?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}</span>
        </Box>
      ),
    },
    {
      field: 'vatAmount',
      headerName: 'VAT Amount',
      flex: 0.9,
      minWidth: 120,
      type: 'number',
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5, width: '100%' }}>
          <img src={riyalIcon} alt="currency" style={{ width: '15px', height: '15px' }} />
          <span>{params.value?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}</span>
        </Box>
      ),
    },
    {
      field: 'totalAmount',
      headerName: 'Total',
      flex: 1,
      minWidth: 130,
      type: 'number',
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => (
        <Box className="font-weight-bold" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5, width: '100%' }}>
          <img src={riyalIcon} alt="currency" style={{ width: '15px', height: '15px' }} />
          <span>{params.value?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}</span>
        </Box>
      ),
    },
  ];

  const summaryCards = [
    {
      title: 'Total Invoices',
      value: totals?.totalInvoices || reportData.length || 0,
      icon: <ReceiptIcon />,
      color: '#4361ee',
      format: (val) => val.toLocaleString(),
    },
    {
      title: 'Total Sales Amount',
      value: totals?.grandTotal || reportData.reduce((sum, item) => sum + (item.grandTotal || 0), 0) || 0,
      icon: <SalesIcon />,
      color: '#2e7d32',
      format: (val) => (
        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
          <img src={riyalIcon} alt="currency" style={{ width: '15px', height: '15px' }} />
          <span>{val.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
        </Box>
      ),
    },
    {
      title: 'Total VAT Collected',
      value: totals?.totalVAT || reportData.reduce((sum, item) => sum + (item.vatAmount || 0), 0) || 0,
      icon: <VATIcon />,
      color: '#ed6c02',
      format: (val) => (
        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
          <img src={riyalIcon} alt="currency" style={{ width: '15px', height: '15px' }} />
          <span>{val.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
        </Box>
      ),
    },
  ];

  const handleBack = () => {
    navigate('/reports');
  };

  return (
    <Container maxWidth={false} sx={{ mt: 0.5, px: { xs: 0.5, sm: 1, md: 1, lg: 1 } }}>
      <Box sx={{ mb: 0.5, display: 'flex', alignItems: 'center' }}>
        <IconButton onClick={handleBack} sx={{ mr: 1.5, bgcolor: 'rgba(67, 97, 238, 0.1)' }}>
          <BackIcon />
        </IconButton>
        <Box>
          <Typography variant="h5" fontWeight="700" gutterBottom>
            VAT Sales Report
          </Typography>
          <Typography variant="body2" color="text.secondary">
            View your sales VAT details and summaries over time.
          </Typography>
        </Box>
      </Box>

      <Paper elevation={0} sx={{ p: 1, mb: 0.5, border: '1px solid', borderColor: 'divider', borderRadius: 0 }}>
        <Typography variant="caption" fontWeight="600" sx={{ mb: 1, display: 'block', fontSize: '0.75rem' }}>
          Filters
        </Typography>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Grid container spacing={1} sx={{ mt: 0 }}>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                select
                label="Group By"
                value={filters.groupBy}
                onChange={(e) => handleFilterChange('groupBy', e.target.value)}
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': { borderRadius: 0, height: '36px', fontSize: '0.875rem' },
                  '& .MuiInputLabel-root': { fontSize: '0.875rem' },
                  '& .MuiOutlinedInput-input': { padding: '8px 14px' },
                }}
              >
                <MenuItem value="day">Daily</MenuItem>
                <MenuItem value="week">Weekly</MenuItem>
                <MenuItem value="month">Monthly</MenuItem>
                <MenuItem value="year">Yearly</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                select
                label="Customer"
                value={filters.customerCode}
                onChange={(e) => handleFilterChange('customerCode', e.target.value)}
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': { borderRadius: 0, height: '36px', fontSize: '0.875rem' },
                  '& .MuiInputLabel-root': { fontSize: '0.875rem' },
                  '& .MuiOutlinedInput-input': { padding: '8px 14px' },
                }}
              >
                <MenuItem value="all">All Customers</MenuItem>
                {customers.map((cus) => (
                  <MenuItem key={cus._id} value={cus.code || cus.customerCode}>
                    {cus.customerName || cus.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <DatePicker
                label="Start Date"
                value={filters.startDate}
                onChange={(date) => handleFilterChange('startDate', date)}
                slotProps={{
                  textField: {
                    size: 'small',
                    fullWidth: true,
                    sx: {
                      '& .MuiOutlinedInput-root': { borderRadius: 0, height: '36px', fontSize: '0.875rem' },
                      '& .MuiInputLabel-root': { fontSize: '0.875rem' },
                      '& .MuiOutlinedInput-input': { padding: '8px 14px' },
                    },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <DatePicker
                label="End Date"
                value={filters.endDate}
                onChange={(date) => handleFilterChange('endDate', date)}
                slotProps={{
                  textField: {
                    size: 'small',
                    fullWidth: true,
                    sx: {
                      '& .MuiOutlinedInput-root': { borderRadius: 0, height: '36px', fontSize: '0.875rem' },
                      '& .MuiInputLabel-root': { fontSize: '0.875rem' },
                      '& .MuiOutlinedInput-input': { padding: '8px 14px' },
                    },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Button
                fullWidth
                variant="contained"
                onClick={handleApplyFilters}
                disabled={loading}
                sx={{ borderRadius: 0, height: '36px', textTransform: 'none', fontWeight: 600, fontSize: '0.875rem' }}
              >
                {loading ? <CircularProgress size={20} /> : 'Apply Filters'}
              </Button>
            </Grid>
          </Grid>
        </LocalizationProvider>
      </Paper>

      {totals && (
        <Grid container spacing={1} sx={{ mb: 0.5 }}>
          {summaryCards.map((card, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Paper
                elevation={0}
                sx={{
                  p: 1.5,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 0,
                  borderLeft: `4px solid ${card.color}`,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '8px',
                      bgcolor: `${card.color}15`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: 1.5,
                    }}
                  >
                    {React.cloneElement(card.icon, { sx: { color: card.color, fontSize: 20 } })}
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {card.title}
                    </Typography>
                    <Typography variant="h6" fontWeight="700">
                      {card.format(card.value)}
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      <ReportDataGrid
        data={reportData}
        columns={columns}
        loading={loading}
        onRefresh={fetchReport}
        exportFileName={`vat-sales-report-${new Date().toISOString().split('T')[0]}`}
        pageSize={25}
        height={700}
        getRowId={(row) => row.invoiceNumber || row.id || Math.random().toString()}
        totals={totals}
      />
    </Container>
  );
};

export default VATSalesReport;
